import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import React, { useEffect, useState } from 'react';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import SelectListField from '../../components/Fields/SelectListField';
import { FormConfig } from '../../components/Fields/Types';
import Form from '../../components/Form/Form';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import ProgressBarField from '../../components/ProgressBar/ProgressBarField';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import WorkerProcessStatus from '../../models/WorkerProcessStatus';
import DataService from '../../services/data.service';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';

interface ExportStartModel {
    processName: string
    externalSystemId?: number
}

const SendMessageView: React.FC = () => {
    const exportProcesses = useSelectListOptions('api/v1/selectlist/ExportProcesses', '-- Select process');
    const externalSystems = useSelectListOptions('api/v1/selectlist/ExternalSystems', '-- Select external system');
    const [serverErrors, setServerErrors] = useState<DataResult<ExportStartModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException, notify } = useNotifications();
    const [connection, setConnection] = useState<HubConnection>();
    const [progress, setProgress] = useState<WorkerProcessStatus>({ currentItem: 0, message: '', dateTime: '', processId: '', processName: ' ', processType: '', progress: 0, status: 0, totalItems: 0 });
    const { user } = useAuth();

    const [formData, setFormData] = useState<ExportStartModel>();

    useEffect(() => {
        setFormData({
            processName: '',
            externalSystemId: 0,
        })
    }, [])

    useEffect(() => {
        if (user) {
            let url: string | undefined
            switch (user.environment) {
                case 'dev':
                    url = process.env.REACT_APP_API_ENDPOINT_dev;
                    break;
                case 'tst':
                    url = process.env.REACT_APP_API_ENDPOINT_tst;
                    break;
                case 'prd':
                    url = process.env.REACT_APP_API_ENDPOINT_prd;
                    break;
            }

            const newConnection = new HubConnectionBuilder()
                .withUrl(url + 'hubs/importhub', { accessTokenFactory: () => user.token })
                .withAutomaticReconnect()
                .build();

            setConnection(newConnection);
        }
    }, [user?.token]);

    useEffect(() => {
        if (connection) {
            connection.start()
                .then(result => {
                    console.log('Connected to import hub!');

                    connection.on('HandleProgressChanged', p => {
                        setProgress(p)
                        console.log('Progress Changed', p)
                    });

                    connection.on('HandleStatusChanged', p => {
                        setProgress(p);
                        console.log('Status changed:', p)
                    });

                    connection.on('HandleCompleted', p => {
                        setProgress(p)
                        console.log('Completed: ', p)
                    });
                })
                .catch(e => {
                    notify("warning", "Unable to connect to export hub.")
                    console.log('Connection to export hub failed: ', e)
                });
        }
    }, [connection]);

    async function submit() {
        if (formData) {
            DataService
                .create<ExportStartModel>(`api/v1/Exports`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'processName', label: 'Process', validationRules: [{ type: 'isRequired' }] },
            { name: 'externalSystemId', label: 'Import from' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedForExportType } = useAuth();
    if (!isAuthorizedForExportType(ApplicationFunctions.ExportItem))
        return (<Unauthorized />);

    return (
        <div className='main-content'>
            <Form title='Send message'>
                <Form.Content >
                    <SelectListField {...getFieldProps('processName')} options={exportProcesses} />
                    <SelectListField {...getFieldProps('externalSystemId')} options={externalSystems} />
                    <ProgressBarField labelProps={{ text: progress.processName, labelSize: 2, required: false }} progress={progress} visible={true} />
                </Form.Content>
                <Form.Footer>
                    <ButtonBar offset={2}>
                        <SaveButton  {...getSubmitButtonProps()} text="Export" />
                    </ButtonBar>
                </Form.Footer>
            </Form>
        </div>
    );
}
export default SendMessageView;